import React, { useEffect, useState } from 'react'
import { X, Sparkles } from 'lucide-react'
import { projectsApi } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'

interface Props {
  raDocumentId:    string
  onClose:         () => void
  onStarted:       (sessionId: string) => void
}

// Cost / time estimates shown to the user before they confirm the run.
// The cost number comes from the backend (server-side estimate against
// the cost cap); the time range is rough and code-side-only.

export const AutoMapConfirmModal: React.FC<Props> = ({ raDocumentId, onClose, onStarted }) => {
  const { error: showError } = useToast()
  const [loading,  setLoading]  = useState(true)
  const [starting, setStarting] = useState(false)
  const [estimate, setEstimate] = useState<{
    unmatched_count:    number
    estimated_cost_usd: number
    cost_cap_usd?:      number
    already_mapped?:    boolean
  } | null>(null)

  useEffect(() => {
    let cancel = false
    projectsApi.estimateAutoMap(raDocumentId)
      .then(e => { if (!cancel) setEstimate(e) })
      .catch((err: any) => { if (!cancel) showError(err?.message ?? 'Failed to load the auto-map estimate') })
      .finally(() => { if (!cancel) setLoading(false) })
    return () => { cancel = true }
  }, [raDocumentId, showError])

  const handleStart = async () => {
    setStarting(true)
    try {
      const { session_id } = await projectsApi.startAutoMap(raDocumentId)
      onStarted(session_id)
    } catch (err: any) {
      showError(err?.message ?? 'Failed to start auto-map')
      setStarting(false)
    }
  }

  // Crude time estimate: 6 seconds median per assessment at concurrency 1.
  const formatTime = (n: number) => {
    if (n <= 0) return '—'
    const lo = Math.max(1, Math.round(n * 3 / 60))
    const hi = Math.max(lo + 1, Math.round(n * 8 / 60))
    return `${lo}-${hi} minutes`
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-500" />
            Auto-map with AI
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          {loading ? (
            <div className="text-slate-400 py-6 text-center">Estimating…</div>
          ) : estimate?.already_mapped ? (
            <div className="text-slate-600 dark:text-slate-300">
              All Word RA systems in this document are already mapped. No work to do.
            </div>
          ) : estimate ? (
            <>
              <p className="text-slate-600 dark:text-slate-300">
                The AI will attempt to match <strong>{estimate.unmatched_count}</strong> unmatched Word RA system
                {estimate.unmatched_count === 1 ? '' : 's'} to existing project systems for this project.
              </p>

              <div className="rounded border border-slate-200 dark:border-slate-700 divide-y divide-slate-200 dark:divide-slate-700">
                <Row label="Estimated time"  value={formatTime(estimate.unmatched_count)} />
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                You'll review and accept each suggestion before any mapping is saved.
                Nothing is committed automatically.
              </p>
            </>
          ) : (
            <div className="text-rose-500 text-sm">Could not load estimate.</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <Button variant="outline" size="sm" onClick={onClose} disabled={starting}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleStart}
            disabled={loading || starting || !estimate || !!estimate.already_mapped || estimate.unmatched_count === 0}
          >
            {starting ? 'Starting…' : 'Start Auto-map'}
          </Button>
        </div>
      </div>
    </div>
  )
}

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between px-3 py-2 text-xs">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className="font-medium text-slate-800 dark:text-slate-100 tabular-nums">{value}</span>
  </div>
)
